import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { components } from '../api/schema';
import { Modal, Pill } from '../components';
import RemoteCertificate from './RemoteCertificate';
import { NativeHelp, type Launch, type NativeLaunch } from './RemoteSession';
import './remoteLauncher.css';

type Connection = components['schemas']['RemoteConnection'];

/** The message a launcher sends the session tab it opened: never in a URL. */
export type SessionHandoff = { type: 'heimdall.remote.launch'; launch: Launch; name: string };

const rememberKey = (id: string) => `heimdall.remote.username:${id}`;
function remembered(id: string): string { try { return localStorage.getItem(rememberKey(id)) ?? ''; } catch { return ''; } }
function remember(id: string, username: string) { try { if (username) localStorage.setItem(rememberKey(id), username); } catch { /* a convenience only */ } }

/**
 * Opening a remote connection: a small dialog for what the protocol needs,
 * then the session in a new tab, or the user's own SSH client.
 *
 * The session is created here, and the new tab is handed its one-time ticket
 * by message once it says it is ready, so the ticket never appears in a URL.
 * The tab is opened as the button is pressed, which pop-up blockers allow.
 */
export default function RemoteLauncher({ connection: c, prefix = '', shardName, nativeAvailable, onClose, onChanged }: { connection: Connection; prefix?: string; shardName?: string; nativeAvailable: boolean; onClose: () => void; onChanged?: () => void }) {
  const ssh = c.protocol === 'ssh', rdp = c.protocol === 'rdp', web = c.protocol === 'http' || c.protocol === 'https';
  const [where, setWhere] = useState<'browser' | 'client'>('browser');
  const [username, setUsername] = useState(() => remembered(c.id)), [password, setPassword] = useState(''), [domain, setDomain] = useState('');
  const [privateKey, setPrivateKey] = useState(''), [passphrase, setPassphrase] = useState(''), [hostKey, setHostKey] = useState(''), [skipVerify, setSkipVerify] = useState(false), [savedTrust, setSavedTrust] = useState(false);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [native, setNative] = useState<NativeLaunch | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const needsUser = (ssh && where === 'browser') || rdp || (ssh && where === 'client');
  const base = `${prefix}/remote-connections/${c.id}/launch`;

  async function launchClient() {
    const n = await api.post<NativeLaunch>(base, { expected_revision: c.revision, username, password, host_key: hostKey, native: true });
    setPassword(''); remember(c.id, username); setNative(n);
    // Hands the link to whatever the OS has registered for ssh://.
    window.location.assign(n.ssh_url);
  }

  async function launchBrowser() {
    const tab = window.open('/access/session', '_blank');
    if (!tab) throw new Error('The browser blocked the new tab. Allow pop-ups for this site and try again.');
    // Listen from the moment the tab opens: it may say it is ready before
    // the session exists. Only that tab, only on this origin.
    let onMessage: (e: MessageEvent) => void = () => {};
    const ready = new Promise<void>(resolve => {
      onMessage = e => { if (e.origin === window.location.origin && e.source === tab && e.data === 'heimdall.remote.ready') resolve(); };
      window.addEventListener('message', onMessage);
    });
    try {
      const width = Math.max(320, Math.min(web ? 2560 : 1920, window.innerWidth));
      const height = Math.max(240, Math.min(web ? 1440 : 1080, window.innerHeight - 56));
      let launch: Launch;
      try {
        launch = await api.post<Launch>(base, { expected_revision: c.revision, username, password, domain, host_key: hostKey, private_key: privateKey, passphrase, skip_verify: skipVerify, width, height });
      } catch (e) { tab.close(); throw e; }
      setPassword(''); setPrivateKey(''); setPassphrase(''); remember(c.id, username);
      let timer = 0;
      const late = new Promise<never>((_, reject) => { timer = window.setTimeout(() => reject(new Error('The session tab did not open in time. Try again.')), 20000); });
      await Promise.race([ready, late]).finally(() => window.clearTimeout(timer));
      const handoff: SessionHandoff = { type: 'heimdall.remote.launch', launch, name: c.name };
      tab.postMessage(handoff, window.location.origin);
    } finally {
      window.removeEventListener('message', onMessage);
    }
    onClose();
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(''); setNative(null);
    try { if (ssh && where === 'client') await launchClient(); else await launchBrowser(); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setBusy(false); }
  }

  const unavailable = !c.enabled ? 'This connection is disabled.' : !c.shard_id ? 'Assign a shard to this connection first.' : '';
  return <Modal title={`Open ${c.name}`} onClose={onClose}>
    <form className="remote-launcher" onSubmit={submit}>
      <p className="remote-launcher-target"><Pill>{c.protocol.toUpperCase()}</Pill> <code>{c.host}:{c.port}{c.web_path || ''}</code>{shardName && <span className="muted"> · through {shardName}</span>}</p>
      {unavailable && <p className="banner warn" role="status">{unavailable}</p>}
      {ssh && nativeAvailable && <div className="remote-launcher-where" role="radiogroup" aria-label="Open in">
        <label className={where === 'browser' ? 'on' : ''}><input type="radio" name="where" checked={where === 'browser'} onChange={() => setWhere('browser')} /><strong>In the browser</strong><small>A terminal in a new tab</small></label>
        <label className={where === 'client' ? 'on' : ''}><input type="radio" name="where" checked={where === 'client'} onChange={() => setWhere('client')} /><strong>In my SSH client</strong><small>Terminal, PuTTY, SecureCRT…</small></label>
      </div>}
      {needsUser && <label>Username<input ref={first} required autoComplete="username" value={username} onChange={e => setUsername(e.target.value)} /></label>}
      {needsUser && <label>Password<input type="password" autoComplete="current-password" value={password} placeholder={ssh && where === 'client' ? 'Leave blank to type it in your client' : undefined} onChange={e => setPassword(e.target.value)} /></label>}
      {rdp && <label>Domain (optional)<input value={domain} onChange={e => setDomain(e.target.value)} /></label>}
      {web && <p className="muted">The device opens in an isolated browser in a new tab. Log in on its page.</p>}
      {(ssh || web || rdp) && <details className="remote-launcher-advanced"><summary>Advanced</summary>
        {ssh && where === 'browser' && <><label>Private key (optional)<textarea aria-label="Private key (optional)" rows={3} value={privateKey} onChange={e => setPrivateKey(e.target.value)} /></label><label>Key passphrase (optional)<input type="password" value={passphrase} onChange={e => setPassphrase(e.target.value)} /></label></>}
        {ssh && <label>SSH host key (optional)<textarea aria-label="SSH host key (optional)" rows={2} placeholder="ssh-ed25519 AAAA…" value={hostKey} onChange={e => setHostKey(e.target.value)} /><small className="muted">Supply the device's OpenSSH public key to refuse a changed or mismatched one; blank connects without verifying it.</small></label>}
        {c.protocol === 'https' && <RemoteCertificate id={c.id} prefix={prefix} revision={c.revision} onChanged={() => onChanged?.()} onSaved={v => { setSavedTrust(v); setSkipVerify(false); }} />}
        {(c.protocol === 'https' || rdp) && <label className="check"><input type="checkbox" disabled={savedTrust} checked={skipVerify} onChange={e => setSkipVerify(e.target.checked)} /> Accept an unverified device certificate for this session</label>}
      </details>}
      {native && <NativeHelp launch={native} />}
      {error && <p className="banner error" role="alert">{error}</p>}
      <div className="row remote-launcher-actions">
        <button className="primary" disabled={busy || !!unavailable || (needsUser && !username)}>{busy ? 'Opening…' : ssh && where === 'client' ? (native ? 'Open again' : 'Open my SSH client') : 'Open in a new tab'}</button>
        <button type="button" onClick={onClose}>{native ? 'Done' : 'Cancel'}</button>
      </div>
      <p className="muted remote-launcher-note">Credentials are used for this session only and never stored. Sessions end after 10 minutes without input or one hour in total.</p>
    </form>
  </Modal>;
}
